import React from 'react';
import { CalculationResult } from '../utils/roiCalculations';
import { fmtMoney, fmtPct } from '../utils/formatters';
interface ResultsSummaryProps {
  results: CalculationResult;
  breakeven?: string;
}
export function ResultsSummary({
  results,
  breakeven
}: ResultsSummaryProps) {
  const roiClass = results.roi >= 0 ? 'text-green-600' : 'text-red-600';
  const opportunityCost = results.totalGrossProfit;
  // Use passed breakeven if provided, otherwise use results.breakeven
  const actualBreakeven = breakeven || results.breakeven;
  // Format breakeven to say "Upon Launch" instead of just "Launch"
  const breakevenText = actualBreakeven === 'Launch' ? 'Upon Launch' : actualBreakeven;
  // Calculate actual monthly investment from the first month's data (excluding Launch row)
  // This represents the typical monthly cost: ad spend + recruiting time cost
  const firstMonthRow = results.rows.find(row => row.period === 'Month 1');
  const monthlyInvestment = firstMonthRow ? firstMonthRow.totalCost : results.totalCostAll / 12;
  return <div className="space-y-6 mb-6" data-id="element-532">
      {/* Opportunity Cost Banner */}
      <div className="bg-amber/10 border-l-4 border-amber p-4 md:p-6 rounded-r-lg shadow-sm" data-id="element-533">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-id="element-534">
          <div data-id="element-535">
            <h3 className="text-lg md:text-xl font-bold text-navy mb-1" data-id="element-536">
              The Cost of Inaction
            </h3>
            <p className="text-sm md:text-base text-gray-700 max-w-2xl" data-id="element-537">
              By not implementing this recruiting system, you will be
              potentially missing out on an additional{' '}
              <span className="font-bold text-navy" data-id="element-538">
                {fmtMoney(opportunityCost)}
              </span>{' '}
              in gross profit over the next 12 months.
            </p>
          </div>
          <div className="text-2xl md:text-3xl font-bold text-amber whitespace-nowrap" data-id="element-539">
            {fmtMoney(opportunityCost)}/yr
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-540">
        {/* Hires / Month */}
        <div className="p-4 md:p-6 rounded bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-541">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold" data-id="element-542">
            Hiring Velocity
          </div>
          <div className="text-2xl md:text-3xl font-bold text-navy" data-id="element-543">
            {results.hiresPerMonth.toLocaleString()}
            <span className="text-lg text-gray-400 font-normal" data-id="element-544">/mo</span>
          </div>
          <div className="text-xs text-gray-500 mt-2" data-id="element-545">
            {results.launchedPerMonth.toFixed(1)} reps launched/mo
          </div>
        </div>

        {/* Monthly Investment */}
        <div className="p-4 md:p-6 rounded bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-546">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold" data-id="element-547">
            Monthly Investment
          </div>
          <div className="text-2xl md:text-3xl font-bold text-navy" data-id="element-548">
            {fmtMoney(monthlyInvestment)}
          </div>
          <div className="text-xs text-gray-500 mt-2" data-id="element-549">
            Ad spend + recruiting time
          </div>
        </div>

        {/* Gross Profit */}
        <div className="p-4 md:p-6 rounded bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-550">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold" data-id="element-551">
            12-Mo Gross Profit
          </div>
          <div className="text-2xl md:text-3xl font-bold text-green-600" data-id="element-552">
            {fmtMoney(results.totalGrossProfit)}
          </div>
          <div className="text-xs text-gray-500 mt-2" data-id="element-553">
            {fmtMoney(results.gpPerSale)} GP per sale
          </div>
        </div>

        {/* ROI */}
        <div className="p-4 md:p-6 rounded bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-554">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold" data-id="element-555">
            12-Month ROI
          </div>
          <div className={`text-2xl md:text-3xl font-bold ${roiClass}`} data-id="element-556">
            {fmtPct(results.roi)}
          </div>
          <div className="text-xs text-gray-500 mt-2" data-id="element-557">
            Net Profit: {fmtMoney(results.totalNetProfit)}
          </div>
        </div>

        {/* Break-Even Point */}
        <div className="p-4 md:p-6 rounded bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-558">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold" data-id="element-559">
            Break-Even Point
          </div>
          <div className="text-2xl md:text-3xl font-bold text-amber" data-id="element-560">
            {breakevenText}
          </div>
          <div className="text-xs text-gray-500 mt-2" data-id="element-561">
            When revenue exceeds investment
          </div>
        </div>
      </div>
    </div>;
}